import type { SequenceFace } from '~/components/Sequence/SequenceNodeFaces';

/** One journey state in the catch-all pile, with how many enrolled people are resting on it. */
export interface SequenceOtherState {
  nodeId: string;
  count: number;
}

/** What a pile needs to render: the headline count, the avatars to show, and the named breakdown. */
export interface SequenceOtherStates {
  total: number;
  sample: SequenceFace[];
  states: SequenceOtherState[];
}

/** How many avatars a pile shows before collapsing into a "+N" — matches the server's own sample cap. */
const SAMPLE_MAX = 6;

/**
 * Fold everyone on an unclaimed journey node into ONE pile: the total, a capped avatar sample, and the
 * named per-state breakdown, all derived from the SAME biggest-first ordering.
 *
 * That shared ordering is the point. `byNode` arrives in Postgres `GROUP BY` order, which is a hash
 * aggregate — arbitrary, and not stable between polls. Sorting only the breakdown while slicing the
 * sample off the raw order would show a rep "Waiting for working hours 41" beside six faces taken from
 * whichever state Postgres happened to emit first, so clicking a face lands on someone in a different
 * state than the one they just read. Sorting once, up front, keeps the two halves describing the same people.
 *
 * Shared by both sequence graphs rather than copied into each: the editor's pile excludes nodes claimed by
 * an authored step, the full-journey pile excludes nodes the diagram draws, but past that predicate the
 * derivation is identical — and a hand-copied version of it goes stale exactly the way the pile it feeds is
 * meant to catch.
 */
export function otherStatesFrom(
  byNode: Record<string, { count: number; sample: SequenceFace[] }>,
  isOrphan: (nodeId: string) => boolean,
): SequenceOtherStates {
  const orphans = Object.entries(byNode)
    .filter(([nodeId]) => isOrphan(nodeId))
    .sort(([aId, a], [bId, b]) => b.count - a.count || aId.localeCompare(bId));
  return {
    total: orphans.reduce((sum, [, f]) => sum + f.count, 0),
    sample: orphans.flatMap(([, f]) => f.sample).slice(0, SAMPLE_MAX),
    states: orphans.map(([nodeId, f]) => ({ nodeId, count: f.count })),
  };
}
